import { Injectable } from '@angular/core';
import {
  each,
  extend,
  filter,
  find,
  findIndex,
  get,
  isArray,
  isEmpty,
  isString,
  map,
  remove,
  toLower
} from 'lodash';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import { NavigationProvider } from '@nrwl/navigation-abstract';
import { ApiService } from '../api/api.service';
import { HttpClient } from '@angular/common/http';
import { IEntity, IEntityCache, TimeMode } from '../inputs.datastore/types';
import { InputDatastoreService } from '../inputs.datastore/inputs.datastore.service';
import { NotificationService } from '../../components/notifications/notifications.service';
import { TimestampRange } from '../../components/time-picker/types';
import { UserAuth } from '../user/user.auth';
import { Application } from './applications.model.factory';
import { IApplication, SCRIPT_PRECHECK_STATUS_MESSAGE, TemporaryScriptSuffix } from './types';
import { BenchmarkService, TimeoutWrapperService } from '@nrwl/common-lib';

@Injectable({
  providedIn: 'root'
})
export class ApplicationsService implements IEntityCache {
  deltaTimeFrom: number;
  historicalmonitorcache: Array<Application>;
  realtimemonitorcache: Array<Application>;
  cache: BehaviorSubject<Array<Application>>;
  currentMode: TimeMode = 'real-time';
  updated: Subject<Array<Application>>;
  queryLoading = false;
  scriptPollInterval = 5000; //5secs
  stopPollInterval = 300000; // 5minutes
  totalPollingTime = 0;
  private _isConnectionSuccessful: boolean;
  private readonly unsubscribe: Subject<void> = new Subject();
  private applicationGetURL = '/entity/v1/application?includedeleted=true&limit=2000&sort=id';
  private scriptURL = '/entity/v1/application';
  //eslint-disable-next-line
  private timeoutPromise: any;
  config: any; //eslint-disable-line

  constructor(
    private readonly apiService: ApiService,
    private readonly inputDatastoreService: InputDatastoreService,
    private readonly notificationService: NotificationService,
    private readonly userAuth: UserAuth,
    private readonly navigator: NavigationProvider,
    private readonly benchmarkService: BenchmarkService,
    private readonly http: HttpClient,
    private readonly timeout: TimeoutWrapperService
  ) {
    this.deltaTimeFrom = null;
    this.realtimemonitorcache = [];
    this.historicalmonitorcache = [];

    this.cache = new BehaviorSubject([]);

    this._isConnectionSuccessful = true;
  }

  get authorized(): boolean {
    return this.userAuth.isAuthedFor('READ_WRITE');
  }

  /*eslint-disable*/
  getValuesAsync(): Observable<Array<any>> {
    /*eslint-enable*/
    const apps = this.getValues();
    if (!apps || apps.length === 0) {
      return this.updateApplication(this.realtimemonitorcache).pipe(first());
    }
    return of(apps).pipe(first());
  }

  getEntityName(): string {
    return 'application';
  }

  findById(id: string): IEntity {
    return this.getApplicationById(id);
  }

  findByName(name: string): IEntity {
    return find(this.getApplications(), app => app.getName() === name);
  }

  getByName(name: string): IEntity[] {
    return filter(this.getApplications(), app => app.getName() === name);
  }

  findByEscapedName(name: string): IEntity {
    return find(this.getApplications(), app => app.getEscapedName() === name);
  }

  getByEscapedName(name: string): IEntity[] {
    name = toLower(name);
    // we need to support case insensitive match
    return filter(this.getApplications(), app => toLower(app.getEscapedName()) === name);
  }

  /* eslint-disable */
  getValues(): any[] {
    /* eslint-enable */
    return this.getApplications();
  }

  getApplications(): Array<Application> {
    return this.chooseCacheModel(this.currentMode);
  }

  getApplicationById(appId: string): Application {
    // we are OK, if '8' = 8
    // tslint:disable-next-line: triple-equals
    return find(this.getApplications(), app => app.getId() == appId);
  }

  dispose(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
  updateApplication(
    cache: Array<Application>,
    // eslint-disable-next-line
    params?: TimestampRange,
    // eslint-disable-next-line
    firstRequest?: boolean,
    // eslint-disable-next-line
    mode?: TimeMode
    // eslint-disable-next-line
  ): Observable<any> {
    this.benchmarkService.registerPageEvent('applicationQuery');
    if (this.queryLoading) {
      return this.updated;
    }
    this.updated = new Subject();
    this.queryLoading = true;
    this.apiService
      .get$<any>(this.applicationGetURL, {}) //eslint-disable-line
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          this.benchmarkService.logPageEventTime('Application query', 'applicationQuery');
          this.benchmarkService.registerPageEvent('applicationDataManipulation');
          this.queryLoading = false;
          if (response && response.content) {
            // the service is now used as entity cache for all types of applications
            // therefore, if you are interested to get user-defined apps only, use the filter
            // ::getUserDefinedAppsOnly
            const responseData = this.transformResponse(response.content);
            this.assignApplicationToCacheList(cache, responseData);
            this._isConnectionSuccessful = true;
            this.benchmarkService.logPageEventTime(
              'Application data',
              'applicationDataManipulation'
            );
            this.updated.next(this.getApplications());
            return this.chooseCacheModel(this.currentMode);
          }
        },
        error => {
          this.queryLoading = false;
          console.warn(`Could not get applications: ${error}`);
        },
        () => {
          this.queryLoading = false;
          this.updated.complete();
        }
      );

    return this.updated;
  }

  getUserDefinedAppsOnly(appList: Array<Application>): Array<Application> {
    return appList.filter(app => !app.isDeleted && app.isUserDefined());
  }

  /* eslint-disable */
  transformResponse(dataIn: any): Array<Application> {
    /* eslint-enable */
    dataIn = isString(dataIn) ? JSON.parse(dataIn) : dataIn;
    const response = [];
    if (dataIn !== null && isArray(dataIn)) {
      dataIn.forEach(result => {
        const configIn = <IApplication>{};
        extend(configIn, result);
        const applicationObj: Application = new Application(configIn);
        response.push(applicationObj);
      });
    }
    return response.sort((appA, appB) => {
      const name1 = appA.escapedName.toLowerCase();
      const name2 = appB.escapedName.toLowerCase();
      return name1 < name2 ? -1 : name1 > name2 ? 1 : 0;
    });
  }

  /* eslint-disable */
  populateAppServer(appServers: any): any {
    /* eslint-enable */
    const combinedAppServer = {
      ips: [],
      ports: [],
      protocols: []
    };
    each(appServers, server => {
      combinedAppServer.ips.push(server.ips);
      each(server.ports, port => {
        if (port && port.portValueType) {
          const definedPort =
            port.portValueType === 'single' ? port.fixed : port.first + ', ' + port.last;
          combinedAppServer.ports.push(definedPort);
        }
      });
      combinedAppServer.protocols.push(server.ipProtocols.join(', '));
    });
    return combinedAppServer;
  }

  assignApplicationToCacheList(cache: Array<Application>, data: Array<Application>): void {
    each(data, item => {
      const index = this.findIndexById(cache, item.getId());
      // If exist, update only application description
      if (index !== -1) {
        this.findApplicationByIndex(cache, index).setApplications(item.getApplications());
      } else {
        cache.push(item);
      }
    });
    each(cache, item => {
      if (item !== undefined) {
        const index = this.findIndexById(data, item.getId());
        if (index === -1) {
          this.removeApplicationCacheByID(cache, item.getId());
        }
      }
    });
    this.cache.next(this.chooseCacheModel(this.currentMode));
  }

  updateApplicationCache(cache: Array<Application>, data: Array<Application>): void {
    each(data, item => {
      const index = this.findIndexById(cache, item.getId());
      if (index !== -1) {
        this.findApplicationByIndex(cache, index).setApplications(item.getApplications());
      }
    });
    this.cache.next(this.chooseCacheModel(this.currentMode));
  }

  removeApplicationCacheByID(cache: Array<Application>, applicationId: string): Array<Application> {
    return remove(cache, application => application.getId() === applicationId);
  }

  findApplicationByIndex(cache: Array<Application>, index: number): Application {
    return get(cache, `[${index}]`, null);
  }

  chooseCacheModel(mode?: TimeMode): Array<Application> {
    if (mode === 'historical') {
      return this.historicalmonitorcache;
    } else {
      return this.realtimemonitorcache;
    }
  }

  findIndexById(cache: Array<Application>, uid: string): number {
    return findIndex(cache, application => uid === application.getId());
  }

  clearRealtimeCache(): void {
    this.realtimemonitorcache.length = 0;
  }

  findApplicationCacheByID(cache: Array<Application>, applicationId: string): Application {
    return find(cache, application => application.getId() === applicationId);
  }

  /* eslint-disable */
  removeApplication$(url: any, data: any): Observable<any> {
    /* eslint-enable */
    return this.apiService.delete$<any>(url, data, {}); //eslint-disable-line
  }

  /*eslint-disable*/
  removeApplicationResponse(response: any): void {
    /*eslint-enable*/
    const result: Array<Application> = response;
    if (response && response[0].errors) {
      const errorMsg = 'There is problem while removing an application';
      this.notificationService.onFail(errorMsg);
    } else if (!isEmpty(result)) {
      const applicationToRemove = map(result, 'success.id');
      this.removeApplicationsFromCache(applicationToRemove);
      const successMsg = 'Application removed successfully';
      this.notificationService.onSuccess(successMsg);
    }
  }

  /* eslint-disable */
  updateApplicationData$(url: any, data: any): Observable<any> {
    /* eslint-enable */
    return this.apiService.put$<any>(url, data, {}); //eslint-disable-line
  }

  /* eslint-disable */
  addApplicationResponse() {
    this.updateApplication(this.getApplications());
  }

  updateApplicationResponse(response) {
    const transfResponse = this.transformResponse([response]);
    this.updateApplicationCache(this.getApplications(), transfResponse);
  }

  removeApplicationsFromCache(data: Array<string>): void {
    const allApplication = this.getApplications();
    each(data, id => {
      this.removeOneApplication(allApplication, id);
    });

    this.cache.next(this.chooseCacheModel(this.currentMode));
  }

  removeOneApplication(allApplications: Array<Application>, id: string): void {
    this.removeApplicationCacheByID(allApplications, id);
  }

  // Clear each page widget component queue
  clearQueues() {
    this.inputDatastoreService.clearQueues();
  }

  navigateToWizard(wizardId, defaultParams = {}) {
    this.navigator.navigateToWizard([wizardId], {
      queryParams: {
        defaultParams: JSON.stringify(defaultParams)
      }
    });
  }

  startPollingScriptStatus(params, clearPollingInterval = false): void {
    if (this.timeoutPromise) {
      this.timeout.cancel(this.timeoutPromise);
    }
    if (clearPollingInterval) {
      this.totalPollingTime = 0;
    }
    //stop polling time after 5minutes
    if (this.totalPollingTime >= this.stopPollInterval) {
      return;
    }
    this.getScriptDetails$(params)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          if (response.isPreCheckPassed) {
            this.notificationService.onSuccess(SCRIPT_PRECHECK_STATUS_MESSAGE.SUCCESSFULL);
          } else if (response.failurecode) {
            this.notificationService.onFail(SCRIPT_PRECHECK_STATUS_MESSAGE.ERROR);
          } else {
            this.timeoutPromise = this.timeout.add(() => {
              this.totalPollingTime += this.scriptPollInterval;
              this.startPollingScriptStatus(params);
            }, this.scriptPollInterval);
          }
        },
        error => {
          //temporary script might be removed, We need to look on actual script
          const isTemporaryScript = params.name.includes(TemporaryScriptSuffix);
          if (error.status === 404 && isTemporaryScript) {
            params.name = params.name.split('_')[0];
            this.startPollingScriptStatus(params);
          } else {
            this.notificationService.onFail(SCRIPT_PRECHECK_STATUS_MESSAGE.ERROR);
          }
        }
      );
  }

  getScripts$(id: number): Observable<any> {
    return this.apiService.get$(`${this.scriptURL}/${id}/script/all/`, {});
  }

  getScriptDetails$(params: any): Observable<any> {
    return this.apiService.get$(`${this.scriptURL}/${params.id}/script/${params.name}`, {});
  }

  deleteScript$(body: any): Observable<any> {
    return this.apiService.delete$(`${this.scriptURL}/${body.id}/script/${body.name}`);
  }

  deleteBulkScript$(body: any): Observable<any> {
    return this.apiService.delete$(`${this.scriptURL}/${body.id}/script/bulk`, body.scriptNames);
  }
}
